import { Button, Form, InputNumber, Modal } from 'antd';
import { useEffect, useState } from 'react';

import { TimePicker } from 'app/components/fields/dayjsPickers';
import type { DayRoomAccessFormValue } from 'app/utils/dayRoomAccess';
import { dayjs } from 'app/utils/dayjs';
import type { Dayjs } from 'app/utils/dayjs';
import { pluralize } from 'app/utils/strings';

import styles from './DayRoomAccessModal.module.scss';

const TIME_FORMAT = 'HH:mm';
const MAX_DURATION_HOURS = 24;
const MINUTE_STEP = 15;

type ModalFormValues = {
  durationHours?: number;
  startTime?: Dayjs;
};

type Props = {
  open: boolean;
  value: DayRoomAccessFormValue | null;
  onSave: (value: DayRoomAccessFormValue) => Promise<void>;
  onClose: () => void;
};

export const DayRoomAccessModal = ({ open, value, onSave, onClose }: Props) => {
  const [form] = Form.useForm<ModalFormValues>();
  const [isSaving, setIsSaving] = useState(false);
  const durationHours = Form.useWatch('durationHours', form);
  const startTime = Form.useWatch('startTime', form);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        durationHours: value?.durationHours,
        startTime: value ? dayjs(value.startTime, TIME_FORMAT) : undefined,
      });
    }
  }, [open, value, form]);

  const endTime =
    startTime && durationHours
      ? startTime.add(durationHours, 'hour')
      : undefined;

  const handleSave = async () => {
    const values = await form.validateFields();

    if (values.durationHours === undefined || values.startTime === undefined) {
      return;
    }

    setIsSaving(true);

    try {
      await onSave({
        durationHours: values.durationHours,
        startTime: values.startTime.format(TIME_FORMAT),
      });
      onClose();
    } catch {
      return;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title="Set up day access"
      open={open}
      onCancel={onClose}
      width={572}
      centered
      destroyOnClose
      footer={[
        <Button
          key="save"
          type="primary"
          loading={isSaving}
          onClick={handleSave}
        >
          Save
        </Button>,
      ]}
    >
      <div className={styles.notice}>
        This configuration is set at hotel level and applies to all room access
        added to a daytime package.
      </div>

      <Form
        form={form}
        layout="horizontal"
        colon={false}
        labelCol={{ flex: '80px' }}
        className={styles.form}
      >
        <Form.Item label="Duration" required>
          <div className={styles.durationField}>
            <Form.Item
              name="durationHours"
              noStyle
              rules={[{ required: true, message: 'Duration is required' }]}
            >
              <InputNumber
                min={1}
                max={MAX_DURATION_HOURS}
                precision={0}
                className={styles.durationInput}
              />
            </Form.Item>
            <span>{pluralize(durationHours, 'hour', 'hours', false)}</span>
          </div>
        </Form.Item>

        <Form.Item
          label="Start time"
          name="startTime"
          rules={[{ required: true, message: 'Start time is required' }]}
        >
          <TimePicker
            format={TIME_FORMAT}
            minuteStep={MINUTE_STEP}
            className={styles.timePicker}
          />
        </Form.Item>

        <Form.Item label="End time" required>
          <TimePicker
            format={TIME_FORMAT}
            value={endTime}
            disabled
            className={styles.timePicker}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
